/**
 * Focused drill surface — one spot at a time, the coach verdict + any
 * follow-up discussion in the same frame, "Next spot" dealing a fresh one
 * in place (no growing page scroll). The right rail is a per-spot
 * conversation pane: the user's action, the coach's verdict, and any
 * "walk through the reasoning" / free-form follow-ups stack there. It never
 * leaves the focused view — "walk through" continues here, not in /chat.
 *
 * Reuses the entire drill ENGINE (`streamChat` SSE, SpotCard, the
 * structured-block parser, the daily-challenge ladder); only the
 * presentation differs. The backend contract is byte-identical to
 * ChatView — same `/chat` request, same structured-block output.
 *
 * State machine:
 *   loading-spot     → kickoff or "Next spot" in flight; awaiting a spot
 *   awaiting-action  → spot shown; user picks an action
 *   streaming        → a coach reply (verdict or follow-up) is streaming
 *   ready            → reply committed; transcript + CTAs + follow-up input
 *   error            → couldn't load; Retry re-requests a spot
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CapReachedError,
  formatActionChoice,
  streamChat,
  type ChatStreamRequest,
} from "@/shared/api";
import { useUpgradeModal } from "@/features/billing/components/UpgradeModal";
import { getDailyChallenge } from "@/features/daily/lib/dailyChallenge";
import {
  getGamification,
  type Gamification,
} from "@/features/gamification/lib/gamification";
import type { SpotAction, SpotPayload } from "@/shared/models";
import { invalidateAfterTurn } from "@/shared/queryInvalidation";
import { nextPhaseFromStream } from "@/features/drills/lib/drillStream";
import { DrillFrame } from "@/features/drills/components/DrillFrame";
import { SheetModal } from "@/features/chat/components/SheetModal";

export type DrillPhase =
  | "loading-spot"
  | "awaiting-action"
  | "streaming"
  | "ready"
  | "error";

/** One turn in the per-spot conversation rail. */
export interface DrillMessage {
  role: "user" | "assistant";
  content: string;
}

/** Verbose phrasing the backend's `_is_spot_request_turn` heuristic
 *  recognizes as "serve a fresh spot" (matched by prefix). The plain
 *  "Next spot" would, on a library miss, make the coach scold the user
 *  for skipping an unactioned spot. Shared with SpotCard's post-report
 *  flow. */
const NEXT_SPOT_MESSAGE =
  "Give me a new spot to drill. I'm passing on the previous one.";

/** Canned "go deeper" follow-up — same prompt the daily footer used. */
const WALK_THROUGH_MESSAGE = "Walk me through the reasoning on that hand.";

interface DrillViewProps {
  /** Mode for the session (spot_drill / archetype_drill / …). */
  initialMode?: string;
  /** The kickoff message fired on mount to serve the first spot. */
  kickoffMessage?: string;
  title?: string;
  /** Structured drill metadata threaded into every request. */
  drillTarget?: string;
  drillDifficulty?: "foundational" | "default" | "edge_case";
  /** Today's Drill: the user's local date (YYYY-MM-DD). When set, the
   *  turn is part of the shared daily ladder (cap-exempt, pinned spot,
   *  streak on completion) and the frame shows ladder navigation rather
   *  than a "Next spot" button (which would re-serve the pinned spot). */
  dailyChallengeDate?: string;
}

export function DrillView({
  initialMode,
  kickoffMessage,
  title,
  drillTarget,
  drillDifficulty,
  dailyChallengeDate,
}: DrillViewProps) {
  const [phase, setPhase] = useState<DrillPhase>("loading-spot");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [currentSpot, setCurrentSpot] = useState<SpotPayload | undefined>();
  // The conversation for the CURRENT spot — user action, verdict, follow-ups.
  // Cleared when a new spot is dealt.
  const [transcript, setTranscript] = useState<DrillMessage[]>([]);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [spotsDone, setSpotsDone] = useState(0);
  const [openSheetId, setOpenSheetId] = useState<string | null>(null);
  // XP earned by the just-graded turn, shown as a "+N XP" tick in the rail.
  // Derived by diffing the persisted total before/after the turn.
  const [xpDelta, setXpDelta] = useState(0);
  const prevXpRef = useRef<number | null>(null);

  const upgradeModal = useUpgradeModal();
  const queryClient = useQueryClient();
  // Belt-and-suspenders against a double-fire: a button click slipping
  // through before React state settles would otherwise fire a second
  // concurrent /chat on the same conversation.
  const isStreamingRef = useRef(false);
  const hasStartedRef = useRef(false);
  // The phase to fall back to if a turn fails (e.g. cap reached).
  const phaseBeforeRef = useRef<DrillPhase>("loading-spot");

  const isDaily = !!dailyChallengeDate;
  const { data: dailyChallenge } = useQuery({
    queryKey: ["daily-challenge"],
    queryFn: getDailyChallenge,
    staleTime: 60_000,
    enabled: isDaily,
  });

  // ---- Kickoff (run once) ----
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    if (kickoffMessage) void runTurn(kickoffMessage, { dealSpot: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runTurn(
    message: string,
    opts?: { dealSpot?: boolean; userVisible?: boolean },
  ) {
    const trimmed = message.trim();
    if (!trimmed) return;
    if (isStreamingRef.current) return;
    isStreamingRef.current = true;

    phaseBeforeRef.current = phase;
    setErrorMessage(null);
    setStreamBuffer("");
    setXpDelta(0);
    prevXpRef.current =
      queryClient.getQueryData<Gamification>(["gamification"])?.totalXp ?? null;

    if (opts?.dealSpot) {
      // Dealing a fresh spot: clear the prior spot's conversation.
      setTranscript([]);
      setPhase("loading-spot");
    } else {
      // A reply-producing turn (action / walk-through / free-form). Show the
      // user's message in the rail unless it's a hidden control message.
      if (opts?.userVisible !== false) {
        setTranscript((t) => [...t, { role: "user", content: trimmed }]);
      }
      setPhase("streaming");
    }

    let buffer = "";
    try {
      const req: ChatStreamRequest = {
        message: trimmed,
        conversationId,
        mode: initialMode,
        drillTarget,
        drillDifficulty,
        dailyChallengeDate,
      };
      for await (const event of streamChat(req)) {
        if (event.kind === "meta") {
          setConversationId(event.conversationId);
        } else if (event.kind === "token") {
          buffer += event.chunk;
          setStreamBuffer(buffer);
        } else if (event.kind === "error") {
          setErrorMessage(event.message);
        }
      }

      const outcome = nextPhaseFromStream(buffer);
      if (outcome.kind === "spot") {
        setCurrentSpot(outcome.spot);
        setTranscript([]);
        setSpotsDone((n) => n + 1);
        setPhase("awaiting-action");
      } else if (outcome.kind === "verdict") {
        setTranscript((t) => [...t, { role: "assistant", content: outcome.text }]);
        setPhase("ready");
      } else {
        // Nothing parseable. Keep the spot actionable / conversation open if
        // we have one; otherwise surface the error state.
        if (currentSpot) {
          setPhase(transcript.length > 0 ? "ready" : "awaiting-action");
        } else {
          setPhase("error");
          if (!errorMessage) {
            setErrorMessage("The coach didn't return a spot. Try again.");
          }
        }
      }
    } catch (err) {
      if (err instanceof CapReachedError) {
        upgradeModal.open({
          window: err.window,
          gateType: err.gateType,
          subscriptionStatus: err.subscriptionStatus,
          planLevel: err.planLevel,
          used: err.used,
          limit: err.limit,
        });
        setPhase(currentSpot ? phaseBeforeRef.current : "error");
      } else {
        setErrorMessage(err instanceof Error ? err.message : "Stream failed");
        setPhase(currentSpot ? phaseBeforeRef.current : "error");
      }
    } finally {
      setStreamBuffer("");
      isStreamingRef.current = false;
      // Billing always; daily when applicable; and the profile cluster —
      // a graded drill turn adds signals, so the Skills/Focus surfaces would
      // otherwise serve a cached pre-threshold state (e.g. "16/25 keep
      // drilling" lingering after the report unlocked). See queryInvalidation.
      invalidateAfterTurn(queryClient, {
        isDaily: !!dailyChallengeDate,
        includeProfile: true,
      });
      // Refresh gamification (XP / achievements) + diff for the per-spot tick.
      // Fetch DIRECTLY (not queryClient.fetchQuery, which can return the
      // cached pre-increment value because the ["gamification"] observers set
      // staleTime: 60s — that made the +XP tick intermittently miss). Then
      // write the fresh result into the cache so the LevelChip + unlock-modal
      // provider update off it.
      try {
        const fresh = await getGamification();
        queryClient.setQueryData(["gamification"], fresh);
        const prev = prevXpRef.current;
        if (prev != null && fresh.totalXp > prev) {
          setXpDelta(fresh.totalXp - prev);
        }
        prevXpRef.current = fresh.totalXp;
      } catch {
        // XP meter is non-critical; ignore.
      }
    }
  }

  function onAction(action: SpotAction) {
    if (!currentSpot) return;
    void runTurn(formatActionChoice(action, currentSpot));
  }

  function onNextSpot() {
    void runTurn(NEXT_SPOT_MESSAGE, { dealSpot: true });
  }

  function onWalkThrough() {
    void runTurn(WALK_THROUGH_MESSAGE);
  }

  function onAsk(text: string) {
    void runTurn(text);
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-3rem-5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-screen">
      {title && (
        <div className="px-4 md:px-8 py-3 border-b border-border bg-surface/50 backdrop-blur sticky top-0 z-[5] flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold text-text">{title}</h1>
          {spotsDone > 0 && (
            <span className="text-xs text-text-muted tabular-nums">
              Spot {spotsDone}
            </span>
          )}
        </div>
      )}

      <DrillFrame
        phase={phase}
        spot={currentSpot}
        conversationId={conversationId}
        transcript={transcript}
        streamBuffer={streamBuffer}
        errorMessage={errorMessage}
        xpDelta={xpDelta}
        isDaily={isDaily}
        dailyChallenge={dailyChallenge}
        onAction={onAction}
        onNextSpot={onNextSpot}
        onWalkThrough={onWalkThrough}
        onAsk={onAsk}
        onRetry={onNextSpot}
        onSheetLink={(id) => setOpenSheetId(id)}
      />

      {openSheetId && (
        <SheetModal sheetId={openSheetId} onClose={() => setOpenSheetId(null)} />
      )}
    </div>
  );
}
